import { ChevronDownIcon, ChevronUpIcon, PencilIcon, UsersIcon } from '@heroicons/react/24/outline';
import {
  ActionIcon,
  Badge,
  Box,
  Button,
  Divider,
  Group,
  Paper,
  Tabs,
  Text,
  Tooltip,
} from '@mantine/core';
import { useMemo, useState } from 'react';
import { OpportunityPositionItem } from '../../../models/opportunity.model';
import { useOpportunityShortlistQuery } from '../hooks/useShortlistQuery';
import { PositionBestFitTab } from './PositionBestFitTab';
import { PositionCandidateSearch } from './PositionCandidateSearch';
import { PositionEditModal } from './PositionEditModal';
import { PositionShortlistTab } from './PositionShortlistTab';

interface PositionCardProps {
  p: OpportunityPositionItem;
  opportunityId: number;
  onOpenPreview: (personId: number) => void;
  isArchived: boolean;
}

export const PositionCard = ({ p, opportunityId, onOpenPreview, isArchived }: PositionCardProps) => {
  const [candidatesOpen, setCandidatesOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const { data: shortlistData } = useOpportunityShortlistQuery(opportunityId, p.id);
  const shortlistCount = useMemo(() => shortlistData?.length ?? 0, [shortlistData]);
  const hasSkills =
    p.technologies.length > 0 || p.certifications.length > 0 || p.languages.length > 0;

  return (
    <Paper withBorder p="md" radius="md" style={{ borderLeft: '3px solid var(--mantine-color-brand-5)' }}>
      {/* Header: title + head count + edit button */}
      <Group justify="space-between" align="center" mb={p.description || hasSkills ? 'sm' : 0}>
        <Group gap="sm" align="center">
          <Text fw={700} size="md">{p.title}</Text>
          <Badge
            size="lg"
            variant="filled"
            color="brand.5"
            style={{ fontWeight: 700, fontSize: 13, letterSpacing: 0 }}
          >
            ×{p.headCount}
          </Badge>
        </Group>
        {!isArchived && (
          <ActionIcon
            variant="subtle"
            color="gray"
            size="sm"
            onClick={() => setEditModalOpen(true)}
            aria-label="Edit position"
          >
            <PencilIcon style={{ width: 14, height: 14 }} />
          </ActionIcon>
        )}
      </Group>

      {p.description && (
        <Text size="sm" c="dimmed" mb="sm" style={{ whiteSpace: 'pre-wrap' }}>
          {p.description}
        </Text>
      )}

      {hasSkills && (
        <Box mb="xs">
          <Text fw={500} size="xs" c="dimmed" mb={6}>Requirements</Text>
          <Group gap={6} wrap="wrap">
            {p.technologies.map((t) => (
              <Tooltip
                key={t.canonicalId}
                label={`Weight: ${t.requiredWeight}%${t.requiredLevel ? ` · Min level: ${t.requiredLevel} (${t.requiredLevel === 1 ? 'Foundation' : t.requiredLevel === 2 ? 'Basic' : t.requiredLevel === 3 ? 'Independent' : t.requiredLevel === 4 ? 'Advanced' : 'Expert'})` : ' · Min level: Any'}`}
                withArrow
              >
                <Badge variant={t.requiredWeight > 0 ? 'filled' : 'light'} color="sopraBlue" size="sm">
                  {t.canonicalName}
                </Badge>
              </Tooltip>
            ))}
            {p.certifications.map((c, idx) => (
              <Badge key={c.certificateId ?? `family-${c.certificationFamilyId ?? idx}`} variant="light" color="sopraPurple" size="sm">
                {c.certificateTitle ?? c.certificationFamilyName}
              </Badge>
            ))}
            {p.languages.map((l) => (
              <Tooltip
                key={l.languageId}
                label={`Weight: ${l.requiredWeight}%${l.requiredProficiencyName ? ` · min. ${l.requiredProficiencyName}` : ''}`}
                withArrow
              >
                <Badge variant={l.requiredWeight > 0 ? 'filled' : 'light'} color="sopraGreen" size="sm">
                  {l.languageNativeName}
                </Badge>
              </Tooltip>
            ))}
          </Group>
        </Box>
      )}

      {/* Candidates section — collapsed by default, only renders when opened */}
      <Divider mt="sm" />
      <Button
        variant="subtle"
        color="gray"
        fullWidth
        mt={4}
        px="xs"
        onClick={() => setCandidatesOpen((v) => !v)}
        aria-expanded={candidatesOpen}
        styles={{ inner: { width: '100%' }, label: { width: '100%' } }}
      >
        <Group justify="space-between" w="100%" align="center">
          <Group gap={8} align="center">
            <UsersIcon style={{ width: 14, height: 14 }} />
            <Text size="md" fw={500}>Candidates</Text>
            {shortlistCount > 0 && (
              <Badge size="xs" variant="filled" color="brand.5">
                {shortlistCount} shortlisted
              </Badge>
            )}
          </Group>
          {candidatesOpen
            ? <ChevronUpIcon style={{ width: 14, height: 14 }} />
            : <ChevronDownIcon style={{ width: 14, height: 14 }} />
          }
        </Group>
      </Button>

      {candidatesOpen && (
        <Box mt="sm">
          <Tabs defaultValue="best-fit" keepMounted={false}>
            <Tabs.List>
              <Tabs.Tab value="best-fit">Best Fit</Tabs.Tab>
              <Tabs.Tab value="search">Search Person</Tabs.Tab>
              <Tabs.Tab value="shortlist">Shortlist</Tabs.Tab>
              <Tooltip label="Coming soon" withArrow>
                <Tabs.Tab value="ai-chat" disabled>AI Chat</Tabs.Tab>
              </Tooltip>
            </Tabs.List>
            <Tabs.Panel value="best-fit">
              <PositionBestFitTab opportunityId={opportunityId} positionId={p.id} onOpenPreview={onOpenPreview} />
            </Tabs.Panel>
            <Tabs.Panel value="search">
              <PositionCandidateSearch opportunityId={opportunityId} positionId={p.id} onOpenPreview={onOpenPreview} />
            </Tabs.Panel>
            <Tabs.Panel value="shortlist">
              <PositionShortlistTab opportunityId={opportunityId} positionId={p.id} onOpenPreview={onOpenPreview} />
            </Tabs.Panel>
          </Tabs>
        </Box>
      )}

      {editModalOpen && (
        <PositionEditModal
          position={p}
          opportunityId={opportunityId}
          opened={editModalOpen}
          onClose={() => setEditModalOpen(false)}
        />
      )}
    </Paper>
  );
};
